import React, { FC, FormEvent, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import PageTitle from 'components/shared/page-title/page-title.component';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { getSupportScreenSharesTemplate } from 'apollo/templates/support-screen-share';
import { SupportScreenShare } from 'models/support-screen-share';
import { formatDate } from 'utils/format';

// Waiting customers appear without a reload; the list is a handful of rooms at most.
const POLL_INTERVAL_MS = 5000;

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 70 },
    { type: 'text', width: 160 },
    { type: 'text', width: 160 },
    { type: 'text', width: 110 },
    { type: 'text', width: 90 },
    { type: 'rectangular', width: 70, height: 32 },
];

/** Waiting and active customer screen shares (DD-3715), started from the regular app's help menu. */
const SupportScreenShares: FC = () => {
    const { t: translate } = useTranslation('super-admin');
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const { data, loading } = useQuery<{ supportScreenShares: SupportScreenShare[] }>(getSupportScreenSharesTemplate, {
        pollInterval: POLL_INTERVAL_MS,
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    const shares = data?.supportScreenShares ?? [];
    const isCodeValid = /^\d{6}$/.test(code);

    const openShare = (shareCode: string) => navigate(`/screen-share/${shareCode}`);
    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isCodeValid) openShare(code);
    };

    const renderStatus = (share: SupportScreenShare) => {
        if (!share.isCustomerSharing) return translate('SCREEN_SHARE_STATUS_NOT_SHARING');
        if (share.viewers.length > 0)
            return translate('SCREEN_SHARE_STATUS_WATCHED', { names: share.viewers.join(', ') });
        return translate('SCREEN_SHARE_STATUS_WAITING');
    };

    return (
        <Box>
            <PageTitle headingStyle="h1" title={translate('MENU_SCREEN_SHARE')} />
            <Typography mb={2}>{translate('SCREEN_SHARE_INTRO')}</Typography>
            <Box component="form" onSubmit={handleSubmit} mb={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        id="screen-share-code"
                        label={translate('SCREEN_SHARE_CODE')}
                        value={code}
                        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ inputMode: 'numeric' }}
                        size="small"
                    />
                    <Button type="submit" variant="contained" disabled={!isCodeValid}>
                        {translate('SCREEN_SHARE_OPEN')}
                    </Button>
                </Stack>
            </Box>
            {loading && !data ? (
                <SkeletonTable columns={skeletonColumns} rows={3} />
            ) : shares.length === 0 ? (
                <Typography>{translate('SCREEN_SHARE_NONE')}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('SCREEN_SHARE_CODE')}</TableCell>
                                <TableCell>{translate('SCREEN_SHARE_USER')}</TableCell>
                                <TableCell>{translate('SCREEN_SHARE_ORGANIZATION')}</TableCell>
                                <TableCell>{translate('SCREEN_SHARE_STARTED')}</TableCell>
                                <TableCell>{translate('SCREEN_SHARE_STATUS')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shares.map((share) => (
                                <TableRow key={share.code}>
                                    <TableCell>{share.code}</TableCell>
                                    <TableCell>
                                        {share.userName}
                                        <Typography variant="body2">{share.userEmail}</Typography>
                                    </TableCell>
                                    <TableCell>{share.organizationName}</TableCell>
                                    <TableCell>{formatDate(share.startedAt)}</TableCell>
                                    <TableCell>{renderStatus(share)}</TableCell>
                                    <TableCell align="right">
                                        <Button variant="contained" onClick={() => openShare(share.code)}>
                                            {translate('SCREEN_SHARE_WATCH')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default SupportScreenShares;
